import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { setAxiosTokenInterceptor } from '../data/api-config';
import { isAdminHosted, isDtiHosted, isNativeAppHosted, isRevProHosted, isTKTEdgeHosted, isVictoryLiveHosted } from '../utils/whitelabel-consts';
import {
  AuthUser,
  VictoryLiveExchangeError,
  VictoryLiveExchangeResponse,
  buildVictoryLiveReturnUrl,
  clearPendingVictoryLiveLoginRedirect,
  decodeVictoryLiveJwt,
  getVictoryLiveTokenExpiryMs,
  hasPendingVictoryLiveLoginRedirect,
  isVictoryLiveSessionCookieMissingError,
  isVictoryLiveTokenExpired,
  setPendingVictoryLiveLoginRedirect,
  toAuthUser,
} from './app-auth.utils';

const DEFAULT_VICTORY_LIVE_LOGIN_URL = 'https://one.victorylive.com/sso/authorize';
const DEFAULT_VICTORY_LIVE_TOKEN_EXCHANGE_URL = 'https://one.victorylive.com/api/vl-session-token/exchange';

export interface AppState {
  returnTo?: string;
}

type LogoutOptions = {
  logoutParams?: {
    returnTo?: string;
  };
};

type LoginWithRedirectOptions = {
  appState?: AppState;
};

type AuthContextValue = {
  error?: Error;
  getAccessTokenSilently: () => Promise<string>;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithRedirect: (options?: LoginWithRedirectOptions) => Promise<void>;
  logout: (options?: LogoutOptions) => void;
  user?: AuthUser;
};

type AuthProviderProps = {
  children: ReactNode;
  onRedirectCallback?: (appState?: AppState) => void;
};

type VictoryLiveSession = {
  expiresAt: number | null;
  token: string;
  user: AuthUser;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function resolveReturnToUrl(returnTo?: string) {
  return new URL(returnTo || window.location.href, window.location.origin).toString();
}

function toError(error: unknown) {
  return error instanceof Error ? error : new Error('An unexpected authentication error occurred.');
}

async function buildVictoryLiveExchangeError(response: Response) {
  let responseMessage: string | undefined;

  try {
    const body = (await response.json()) as { Message?: string };
    responseMessage = typeof body?.Message === 'string' ? body.Message : undefined;
  } catch {
    responseMessage = undefined;
  }

  const message = responseMessage
    ? `Victory Live session exchange failed with status ${response.status}: ${responseMessage}`
    : `Victory Live session exchange failed with status ${response.status}.`;

  return new VictoryLiveExchangeError(message, response.status, responseMessage);
}

async function exchangeVictoryLiveToken() {
  const response = await fetch(import.meta.env.VITE_VICTORY_LIVE_TOKEN_EXCHANGE_URL || DEFAULT_VICTORY_LIVE_TOKEN_EXCHANGE_URL, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw await buildVictoryLiveExchangeError(response);
  }

  const body = (await response.json()) as VictoryLiveExchangeResponse;
  const token = body.Data?.BearerToken;

  if (!token) {
    throw new Error('Victory Live session exchange returned no bearer token.');
  }

  const expiresAtFromResponse = body.Data?.Expiration ? Date.parse(body.Data.Expiration) : null;
  const expiresAt = Number.isNaN(expiresAtFromResponse) ? getVictoryLiveTokenExpiryMs(token) : expiresAtFromResponse;

  return {
    expiresAt,
    token,
    user: toAuthUser(decodeVictoryLiveJwt(token)),
  } satisfies VictoryLiveSession;
}

function Auth0ContextBridge({ children }: { children: ReactNode }) {
  const { error, getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, logout, user } = useAuth0();

  const value = useMemo<AuthContextValue>(
    () => ({
      error: error || undefined,
      getAccessTokenSilently,
      isAuthenticated,
      isLoading,
      loginWithRedirect: async (options) => {
        await loginWithRedirect({ appState: options?.appState });
      },
      logout: (options) => {
        logout(options);
      },
      user: user as AuthUser | undefined,
    }),
    [error, getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, logout, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function Auth0AuthProvider({ children, onRedirectCallback }: AuthProviderProps) {
  const domain = isRevProHosted ? import.meta.env.VITE_AUTH_REVPRO_DOMAIN : isTKTEdgeHosted ? import.meta.env.VITE_AUTH_TKTEDGE_DOMAIN : import.meta.env.VITE_AUTH_DOMAIN;
  const clientId = isRevProHosted
    ? import.meta.env.VITE_AUTH_REVPRO_CLIENT_ID
    : isTKTEdgeHosted
      ? import.meta.env.VITE_AUTH_TKTEDGE_CLIENT_ID
      : isDtiHosted
        ? import.meta.env.VITE_AUTH_DTI_CLIENT_ID
        : import.meta.env.VITE_AUTH_CLIENT_ID;
  const redirectUri = window.location.origin + '/auth/callback';
  const audience = isRevProHosted
    ? import.meta.env.VITE_AUTH_REVPRO_AUDIENCE
    : isTKTEdgeHosted
      ? import.meta.env.VITE_AUTH_TKTEDGE_AUDIENCE
      : isDtiHosted
        ? import.meta.env.VITE_AUTH_DTI_AUDIENCE
        : import.meta.env.VITE_AUTH_AUDIENCE;

  if (!(domain && clientId && redirectUri && audience)) {
    return null;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience,
        redirect_uri: redirectUri,
        scope: isAdminHosted ? 'openid profile email admin:global' : 'openid profile email',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      onRedirectCallback={(appState) => onRedirectCallback?.(appState as AppState | undefined)}
    >
      <Auth0ContextBridge>{children}</Auth0ContextBridge>
    </Auth0Provider>
  );
}

function VictoryLiveAuthProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<Error | undefined>();
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | undefined>();

  const applySession = useCallback((session: VictoryLiveSession) => {
    clearPendingVictoryLiveLoginRedirect();
    setAxiosTokenInterceptor(session.token);
    setError(undefined);
    setExpiresAt(session.expiresAt);
    setToken(session.token);
    setUser(session.user);
  }, []);

  const clearSession = useCallback((nextError?: Error) => {
    setAxiosTokenInterceptor('');
    setError(nextError);
    setExpiresAt(null);
    setToken(null);
    setUser(undefined);
  }, []);

  const redirectToLogin = useCallback(
    (options?: LoginWithRedirectOptions) => {
      const redirectUri = resolveReturnToUrl(options?.appState?.returnTo);

      clearSession();
      setPendingVictoryLiveLoginRedirect();
      window.location.assign(buildVictoryLiveReturnUrl(import.meta.env.VITE_VICTORY_LIVE_LOGIN_URL || DEFAULT_VICTORY_LIVE_LOGIN_URL, redirectUri));
    },
    [clearSession],
  );

  useEffect(() => {
    let isMounted = true;

    void exchangeVictoryLiveToken()
      .then((session) => {
        if (!isMounted) {
          return;
        }

        applySession(session);
      })
      .catch((exchangeError) => {
        if (!isMounted) {
          return;
        }

        if (exchangeError instanceof VictoryLiveExchangeError) {
          if (isVictoryLiveSessionCookieMissingError(exchangeError)) {
            redirectToLogin();
            return;
          }

          const nextError = hasPendingVictoryLiveLoginRedirect() ? new Error('Victory Live authentication failed after returning from login.') : undefined;

          clearSession(nextError);
          return;
        }

        clearSession(toError(exchangeError));
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [applySession, clearSession, redirectToLogin]);

  const getAccessTokenSilently = useCallback(async () => {
    if (token && !isVictoryLiveTokenExpired(token, expiresAt)) {
      setAxiosTokenInterceptor(token);
      return token;
    }

    try {
      const session = await exchangeVictoryLiveToken();
      applySession(session);
      return session.token;
    } catch (exchangeError) {
      if (isVictoryLiveSessionCookieMissingError(exchangeError)) {
        redirectToLogin();

        // Navigation will interrupt the current app lifecycle; keep the promise pending
        // so downstream catch handlers do not race the redirect with a second auth action.
        return new Promise<string>(() => {});
      }

      clearSession(exchangeError instanceof VictoryLiveExchangeError ? undefined : toError(exchangeError));
      throw exchangeError;
    }
  }, [applySession, clearSession, expiresAt, redirectToLogin, token]);

  const loginWithRedirect = useCallback(async (options?: LoginWithRedirectOptions) => {
    redirectToLogin(options);
  }, [redirectToLogin]);

  const logout = useCallback(
    (options?: LogoutOptions) => {
      const redirectUri = resolveReturnToUrl(options?.logoutParams?.returnTo || window.location.origin);
      const logoutUrl = import.meta.env.VITE_VICTORY_LIVE_LOGOUT_URL || import.meta.env.VITE_VICTORY_LIVE_LOGIN_URL || DEFAULT_VICTORY_LIVE_LOGIN_URL;

      clearPendingVictoryLiveLoginRedirect();
      clearSession();
      window.location.assign(buildVictoryLiveReturnUrl(logoutUrl, redirectUri));
    },
    [clearSession],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      error,
      getAccessTokenSilently,
      isAuthenticated: !!token && !isVictoryLiveTokenExpired(token, expiresAt),
      isLoading,
      loginWithRedirect,
      logout,
      user,
    }),
    [error, expiresAt, getAccessTokenSilently, isLoading, loginWithRedirect, logout, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

declare global {
  interface Window {
    __BARKER_NATIVE__?: { ready: boolean; accessToken?: string; idToken?: string };
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

function postToNative(message: Record<string, unknown>) {
  window.ReactNativeWebView?.postMessage(JSON.stringify(message));
}

function parseBridgeMessage(data: unknown): { type?: string; [key: string]: unknown } | undefined {
  if (data && typeof data === 'object') {
    return data as { type?: string };
  }

  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as { type?: string };
    } catch {
      return undefined;
    }
  }

  return undefined;
}

function decodeBridgeUser(idToken?: string): AuthUser | undefined {
  if (!idToken) {
    return undefined;
  }

  try {
    return toAuthUser(decodeVictoryLiveJwt(idToken));
  } catch {
    return undefined;
  }
}

// Runs inside the native mobile app's WebView. Instead of driving the Auth0 redirect
// flow (which embedded WebViews block), it receives the access token from the native
// layer — injected as window.__BARKER_NATIVE__ before load, and refreshed on demand
// over the react-native-webview postMessage bridge.
function NativeBridgeAuthProvider({ children }: { children: ReactNode }) {
  const bootstrap = window.__BARKER_NATIVE__;

  // Apply the bootstrap token to axios synchronously, before any child effect can fire a
  // request. React runs child effects before parent effects, so a useEffect would be too late.
  const bootstrapApplied = useRef(false);
  if (!bootstrapApplied.current) {
    bootstrapApplied.current = true;
    if (bootstrap?.accessToken) {
      setAxiosTokenInterceptor(bootstrap.accessToken);
    }
  }

  const [token, setToken] = useState<string | null>(bootstrap?.accessToken ?? null);
  const [user, setUser] = useState<AuthUser | undefined>(() => decodeBridgeUser(bootstrap?.idToken));
  const [error, setError] = useState<Error | undefined>();
  const [isLoading, setIsLoading] = useState(!bootstrap?.accessToken);
  const pendingRefreshes = useRef<{ resolve: (token: string) => void; reject: (reason: Error) => void }[]>([]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const payload = parseBridgeMessage(event.data);

      if (!payload?.type) {
        return;
      }

      if (payload.type === 'auth.token' && typeof payload.accessToken === 'string') {
        const accessToken = payload.accessToken;
        setAxiosTokenInterceptor(accessToken);
        setToken(accessToken);
        setUser(decodeBridgeUser(typeof payload.idToken === 'string' ? payload.idToken : undefined));
        setError(undefined);
        setIsLoading(false);
        pendingRefreshes.current.forEach((entry) => entry.resolve(accessToken));
        pendingRefreshes.current = [];
      } else if (payload.type === 'auth.error') {
        const bridgeError = new Error(typeof payload.message === 'string' ? payload.message : 'Native authentication failed.');
        setError(bridgeError);
        setIsLoading(false);
        pendingRefreshes.current.forEach((entry) => entry.reject(bridgeError));
        pendingRefreshes.current = [];
      } else if (payload.type === 'auth.loggedOut') {
        setAxiosTokenInterceptor('');
        setToken(null);
        setUser(undefined);
        setIsLoading(false);
      }
    }

    window.addEventListener('message', handleMessage);
    // Tell native the SPA is ready to receive a token (covers the webview mounting
    // before native finished resolving stored credentials).
    postToNative({ type: 'auth.ready' });

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const getAccessTokenSilently = useCallback(async () => {
    if (token && !isVictoryLiveTokenExpired(token)) {
      setAxiosTokenInterceptor(token);
      return token;
    }

    return new Promise<string>((resolve, reject) => {
      const entry = { resolve, reject };
      pendingRefreshes.current.push(entry);
      postToNative({ type: 'auth.refresh' });

      window.setTimeout(() => {
        if (!pendingRefreshes.current.includes(entry)) {
          return;
        }
        pendingRefreshes.current = pendingRefreshes.current.filter((pending) => pending !== entry);
        reject(new Error('Native token refresh timed out.'));
      }, 15_000);
    });
  }, [token]);

  const loginWithRedirect = useCallback(async () => {
    postToNative({ type: 'auth.login' });
  }, []);

  const logout = useCallback(() => {
    postToNative({ type: 'auth.logout' });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      error,
      getAccessTokenSilently,
      isAuthenticated: !!token && !isVictoryLiveTokenExpired(token),
      isLoading,
      loginWithRedirect,
      logout,
      user,
    }),
    [error, getAccessTokenSilently, isLoading, loginWithRedirect, logout, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children, onRedirectCallback }: AuthProviderProps) {
  if (isNativeAppHosted) {
    return <NativeBridgeAuthProvider>{children}</NativeBridgeAuthProvider>;
  }

  if (isVictoryLiveHosted) {
    return <VictoryLiveAuthProvider>{children}</VictoryLiveAuthProvider>;
  }

  return <Auth0AuthProvider onRedirectCallback={onRedirectCallback}>{children}</Auth0AuthProvider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  return context;
}

export function withAuthenticationRequired<Props extends object>(
  Component: React.ComponentType<Props>,
  options?: {
    onRedirecting?: () => React.JSX.Element | null;
  },
) {
  function ProtectedComponent(props: Props) {
    const { error, isAuthenticated, isLoading, loginWithRedirect } = useAuth();
    const loginRequested = useRef(false);

    useEffect(() => {
      if (!isLoading && !isAuthenticated && !error && !loginRequested.current) {
        loginRequested.current = true;
        void loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          },
        });
      }
    }, [error, isAuthenticated, isLoading, loginWithRedirect]);

    useEffect(() => {
      if (isAuthenticated || error) {
        loginRequested.current = false;
      }
    }, [error, isAuthenticated]);

    if (error) {
      throw error;
    }

    if (isLoading || !isAuthenticated) {
      return options?.onRedirecting?.() || null;
    }

    return <Component {...props} />;
  }

  ProtectedComponent.displayName = `WithAuthenticationRequired(${Component.displayName || Component.name || 'Component'})`;

  return ProtectedComponent;
}
